import React, { useContext, useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import {
  EmailSearch,
  Identity,
  SearchIdentitiesFilters,
} from "@safetyculture/s12-apis-connect-web/s12/identity/admin/v1/identity_management_pb";
import { Pagination } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import mainStyles from "../../styles";
import SearchDisplayTable from "./components/search-display-table";
import { DocumentSearch } from "./constants";
import { searchIdentities } from "api/endpoints/search";
import { callOptionsHeaders } from "api/helpers";
import DisplayError from "components/shared/display-error";
import { DEFAULT_REGION_CODE } from "constants/index";
import { IdentitySearchQueryContext } from "context/identity-search-context";
import { SnackbarContext } from "context/snackbar-context";
import { objectHasEmptyValues } from "helpers/object-has-empty-values";
import {
  convertUUIDtoS12Id,
  validateS12ID,
  validateUUID,
} from "helpers/s12-helpers";
import { shallowCompare } from "helpers/shallow-compare";
import { IdentitySearchQuery, TableSearchCategory } from "types/search-display";

const DEFAULT_PAGE_SIZE = 20;
const firstPage = new Pagination({
  pageSize: DEFAULT_PAGE_SIZE,
  offset: 0,
});

const IdentitySearch = () => {
  const classes = mainStyles();
  const emailDomainRef = useRef<HTMLInputElement | null>(null);

  const { showError } = useContext(SnackbarContext);

  const { identitySearchQueryState, setIdentitySearchQueryState } = useContext(
    IdentitySearchQueryContext,
  );
  const [identitySearchFormState, setIdentitySearchFormState] = useState(
    identitySearchQueryState,
  );

  const [identities, setIdentities] = useState<Identity[]>([]);
  const [identitiesTotal, setIdentitiesTotal] = useState(0);

  const [pagination, setPagination] = useState<Pagination>(firstPage);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorValidationMessage, setErrorValidationMessage] = useState("");

  const emailDomainFocus = () => {
    if (emailDomainRef && emailDomainRef.current) {
      return emailDomainRef?.current?.focus();
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string,
  ) => {
    let value;
    switch (key) {
      case "identityID":
      case "userID":
        value = event.target.value.trim();
        break;
      case "matchExactEmail":
        value = (event.target as HTMLInputElement).checked;
        break;
      default:
        value = event.target.value;
    }
    setIdentitySearchFormState({
      ...identitySearchFormState,
      [key]: value,
    });
  };

  const validIdentitySearchFields = (searchQuery: IdentitySearchQuery) => {
    if (searchQuery.identityID) {
      if (!validateUUID(searchQuery.identityID)) {
        return false;
      }
    }

    if (searchQuery.userID) {
      const isValidUUID = validateUUID(searchQuery.userID);
      const isValidS12ID =
        validateS12ID(searchQuery.userID) &&
        searchQuery.userID.startsWith(DocumentSearch.User);
      if (!isValidUUID && !isValidS12ID) {
        return false;
      }
    }

    return true;
  };

  const handleIdentitySearch = () => {
    if (objectHasEmptyValues(identitySearchFormState)) {
      setErrorValidationMessage("Empty search input values");
    } else if (!validIdentitySearchFields(identitySearchFormState)) {
      setErrorValidationMessage("Invalid UUID");
    } else {
      setPagination(firstPage);
      setIdentities([]);

      if (shallowCompare(identitySearchFormState, identitySearchQueryState)) {
        // Search again using same query
        searchUsingSearchQuery();
      } else {
        setIdentitySearchQueryState(identitySearchFormState);
      }
    }
  };

  const clearInputs = () => {
    setIdentitySearchFormState({
      identityID: "",
      userID: "",
      emailDomain: "",
      emailLocal: "",
      matchExactEmail: false,
    });
  };

  const clearForm = () => {
    setErrorValidationMessage("");
    clearInputs();
  };

  useEffect(() => {
    setErrorValidationMessage("");
  }, [identitySearchFormState]);

  const makeSearchReq = () => {
    setIsLoading(true);

    const searchFilters = new SearchIdentitiesFilters({
      identityId: identitySearchQueryState.identityID,
      userId:
        identitySearchQueryState.userID &&
        convertUUIDtoS12Id(
          identitySearchQueryState.userID,
          DocumentSearch.User,
        ),
      emailSearch: new EmailSearch({
        local: identitySearchQueryState.emailLocal,
        domain: identitySearchQueryState.emailDomain,
        matchExactEmail: identitySearchQueryState.matchExactEmail,
      }),
    });

    searchIdentities(
      {
        filters: searchFilters,
        pageSize: pagination.pageSize,
        offset: pagination.offset,
      },
      callOptionsHeaders(DEFAULT_REGION_CODE),
    )
      .then((resp) => {
        setIdentities((prev) => [...prev, ...resp.identities]);
        setIdentitiesTotal(resp.totalItems);
      })
      .catch(showError)
      .finally(() => setIsLoading(false));
  };

  const searchUsingSearchQuery = () => {
    if (objectHasEmptyValues(identitySearchQueryState)) return;

    if (
      identitySearchQueryState.identityID !== identitySearchFormState.identityID
    ) {
      setIdentitySearchFormState({
        ...identitySearchFormState,
        identityID: identitySearchQueryState.identityID,
      });
    }

    if (identitySearchQueryState.userID !== identitySearchFormState.userID) {
      setIdentitySearchFormState({
        ...identitySearchFormState,
        userID: identitySearchQueryState.userID,
      });
    }

    makeSearchReq();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleResultsPageUpdate = (newOffset: number, regionCode: string) => {
    setIdentities([]);
    setPagination(
      new Pagination({
        ...pagination,
        offset: newOffset,
      }),
    );

    makeSearchReq();
  };

  // When the search query changes, search
  // eslint-disable-next-line
  useEffect(searchUsingSearchQuery, [identitySearchQueryState]);

  useEffect(() => {
    if (identitySearchFormState.emailLocal.includes("@")) {
      const [emailLocal, emailDomain] =
        identitySearchFormState.emailLocal.split("@");
      setIdentitySearchFormState({
        ...identitySearchFormState,
        emailLocal: emailLocal,
        emailDomain: emailDomain,
      });
      emailDomainFocus();
    }

    if (
      identitySearchFormState.emailLocal == "" &&
      identitySearchFormState.emailDomain == "" &&
      identitySearchFormState.matchExactEmail
    ) {
      setIdentitySearchFormState({
        ...identitySearchFormState,
        matchExactEmail: false,
      });
    }
  }, [identitySearchFormState]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Box
          component="form"
          sx={classes.spaceSearch}
          noValidate
          autoComplete="off"
        >
          <TextField
            id="email-input"
            label="Email"
            value={identitySearchFormState.emailLocal}
            onChange={(event) => handleChange(event, "emailLocal")}
            variant="outlined"
            size="small"
            inputProps={{ "data-anchor": "identity-email-local-input" }}
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                handleIdentitySearch();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "@") {
                event.preventDefault();
                emailDomainFocus();
              }
            }}
            sx={classes.textInput}
          />
          <Typography display="inline" sx={classes.atSymbol}>
            @
          </Typography>
          <TextField
            id="domain-input"
            label="Domain"
            value={identitySearchFormState.emailDomain}
            onChange={(event) => handleChange(event, "emailDomain")}
            variant="outlined"
            size="small"
            inputProps={{ "data-anchor": "identity-email-domain-input" }}
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                handleIdentitySearch();
              }
            }}
            inputRef={emailDomainRef}
            sx={classes.textInput}
          />
          <TextField
            id="identity-id-input"
            label="Identity ID"
            value={identitySearchFormState.identityID}
            onChange={(event) => handleChange(event, "identityID")}
            variant="outlined"
            size="small"
            inputProps={{ "data-anchor": "identity-id-input" }}
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                handleIdentitySearch();
              }
            }}
            sx={classes.textInput}
          />
          <TextField
            id="identity-user-id-input"
            label="User ID"
            value={identitySearchFormState.userID}
            onChange={(event) => handleChange(event, "userID")}
            variant="outlined"
            size="small"
            inputProps={{ "data-anchor": "identity-user-id-input" }}
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                handleIdentitySearch();
              }
            }}
            sx={classes.textInput}
          />
          <FormControlLabel
            control={
              <Switch
                checked={identitySearchFormState.matchExactEmail}
                onChange={(event) => handleChange(event, "matchExactEmail")}
                data-testid="identity-exact-email-search-switch"
                disabled={
                  identitySearchFormState.emailLocal === "" &&
                  identitySearchFormState.emailDomain === ""
                }
              />
            }
            label="Match exact email"
            sx={classes.checkboxLabel}
          />
          <Button
            variant="contained"
            color="primary"
            data-anchor="identity-search-btn"
            data-testid="identity-search-btn"
            onClick={handleIdentitySearch}
            disabled={objectHasEmptyValues(identitySearchFormState)}
          >
            Search
          </Button>
          <Button
            variant="contained"
            color="secondary"
            data-anchor="user-search-clear-btn"
            data-testid="user-search-clear-btn"
            onClick={clearForm}
          >
            Clear
          </Button>
        </Box>

        <DisplayError errorMessage={errorValidationMessage} />

        <SearchDisplayTable
          key="identitySearchDisplayTable"
          tableSearchCategory={TableSearchCategory.IdentitySearch}
          tableHeading="Identities"
          regionCode={DEFAULT_REGION_CODE}
          tableData={identities}
          paginationValues={pagination}
          handleResultsPageUpdate={handleResultsPageUpdate}
          totalItems={identitiesTotal}
          isLoading={isLoading}
        />
      </Grid>
    </Grid>
  );
};

export default IdentitySearch;
